import { isStandaloneLoginRuntime } from '@seller-hermes/utils/runtime';
import type { PortalProfile, PortalSellerGroupContext } from './portal.types';

export const ACTIVE_SELLER_GROUP_HEADER = 'X-Active-Seller-Group';
export const ACTIVE_SELLER_GROUP_COOKIE = 'abbiamo.activeSellerGroupId';
// Remembers which product (LOG/GO/PAY) the last setActiveSellerGroupId() call
// was for. Only consulted as a fallback when the subdomain can't tell us the
// product (localhost, deploy preview) — see detectCurrentProduct() and
// getDashboardProduct() in context-bootstrap.ts.
export const ACTIVE_PRODUCT_COOKIE = 'abbiamo.activeProduct';

// Supported local-runtime replacement for product subdomains. Production
// handoff uses a per-product cookie so LOG/GO/PAY tabs never overwrite each
// other's active seller group.
const PRODUCT_PARAM = 'product';
const SELLER_GROUP_PARAM = 'sg';
// Short-lived: this cookie is only a UX shortcut (remember the last active
// seller group across a hard refresh) and is always revalidated against
// /portal/me on the next bootstrap — it doesn't need to survive long, and a
// short TTL shrinks the window any stale/foreign value could linger in
// (LOG-475, Camada 2).
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 4;
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface LocalPortalContext {
  product: PortalProfile;
  sellerGroupId: string;
}

let activeSellerGroupId: string | null = null;
let activeProduct: PortalProfile | null = null;

// Per-product set of seller group IDs the authenticated user actually has
// access to, per the last /portal/me response. getValidActiveSellerGroupId()
// requires membership in this set before letting a cookie value leave in a
// request header — closes the gap where a stale/foreign-account cookie could
// reach the backend before bootstrap ever validated it (LOG-475).
const validSellerGroupIdsByProduct = new Map<PortalProfile, Set<string>>();

export const setValidSellerGroupIds = (
  product: PortalProfile,
  ids: string[],
): void => {
  validSellerGroupIdsByProduct.set(product, new Set(ids));
};

export const clearValidSellerGroupIds = (): void => {
  validSellerGroupIdsByProduct.clear();
};

const COOKIE_DOMAIN = process.env['NX_COOKIE_DOMAIN'] ?? '.abbiamolog.com';

const canUseSharedCookieDomain = () =>
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith(COOKIE_DOMAIN.replace(/^\./, ''));

// Scoped cookie name per product — prevents LOG/GO/PAY contexts from
// overwriting each other when multiple product tabs are open simultaneously.
const getProductCookieName = (profile: PortalProfile): string =>
  `${ACTIVE_SELLER_GROUP_COOKIE}.${profile}`;

// Infers the active product from the subdomain so a dashboard tab reads its
// own cookie without an explicit profile argument. Mirrors the hostname
// parsing in context-bootstrap.ts's getDashboardProduct.
//
// localhost and deploy previews have no log./go./pay. subdomain to parse, so
// without the getActiveProduct() fallback below this always returned null in
// those runtimes — which meant the product-scoped seller-group cookie (Layer
// 2 in getActiveSellerGroupId) was silently never read outside of prod/sandbox,
// even though the cookie itself was written and present in the browser.
const detectCurrentProduct = (): PortalProfile | null => {
  if (typeof window !== 'undefined') {
    const hostnameParts = window.location.hostname.toLowerCase().split('.');
    if (hostnameParts.includes('log')) return 'LOG';
    if (hostnameParts.includes('go')) return 'GO';
    if (hostnameParts.includes('pay')) return 'PAY';
  }

  return getActiveProduct();
};

// Layered lookup for "which product is this tab on", used as a fallback when
// the hostname doesn't carry it (see detectCurrentProduct above) and by
// getDashboardProduct() in context-bootstrap.ts for the same reason.
export const getActiveProduct = (): PortalProfile | null => {
  // Layer 0: standalone-runtime query-string context always wins, same as
  // getActiveSellerGroupId().
  if (isStandaloneLoginRuntime()) {
    const fromLocation = getLocalPortalContext()?.product;
    if (fromLocation) return fromLocation;
  }

  // Layer 1: in-memory, set by the last setActiveSellerGroupId(id, profile).
  if (activeProduct) return activeProduct;

  // Layer 2: cookie, survives a hard refresh.
  const cookieValue = getCookieValue(ACTIVE_PRODUCT_COOKIE);
  if (isPortalProfile(cookieValue)) {
    activeProduct = cookieValue;
    return cookieValue;
  }

  return null;
};

export const clearActiveSellerGroupId = (): void => {
  activeSellerGroupId = null;
  activeProduct = null;
  clearValidSellerGroupIds();

  if (typeof document === 'undefined') return;

  const namesToClear: string[] = [
    ACTIVE_SELLER_GROUP_COOKIE,
    getProductCookieName('LOG'),
    getProductCookieName('GO'),
    getProductCookieName('PAY'),
    ACTIVE_PRODUCT_COOKIE,
  ];

  for (const name of namesToClear) {
    const attributes = [`${name}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax'];
    if (canUseSharedCookieDomain()) {
      attributes.push(`Domain=${COOKIE_DOMAIN}`, 'Secure');
    }
    document.cookie = attributes.join('; ');
  }
};

const isPortalProfile = (value: string | null): value is PortalProfile =>
  value === 'LOG' || value === 'GO' || value === 'PAY';

export const getLocalPortalContextFromSearch = (
  search: string,
): LocalPortalContext | null => {
  const params = new URLSearchParams(search);
  const product = params.get(PRODUCT_PARAM);
  const sellerGroupId = params.get(SELLER_GROUP_PARAM);

  if (!isPortalProfile(product) || !sellerGroupId) {
    return null;
  }

  return { product, sellerGroupId };
};

export const getLocalPortalContext = (): LocalPortalContext | null => {
  if (typeof window === 'undefined') return null;
  return getLocalPortalContextFromSearch(window.location.search);
};

export const getActiveSellerGroupIdFromLocation = (): string | null =>
  getLocalPortalContext()?.sellerGroupId ?? null;

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${name}=`));

  if (!cookie) return null;

  return decodeURIComponent(cookie.slice(name.length + 1));
};

const writeCookie = (name: string, value: string): void => {
  const attributes = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];

  if (canUseSharedCookieDomain()) {
    attributes.push(`Domain=${COOKIE_DOMAIN}`, 'Secure');
  }

  document.cookie = attributes.join('; ');
};

// profile is passed by every navigation/handoff call site (context-bootstrap,
// the sidebar context switcher, the local login-context page) so the cookie
// is product-scoped. Without profile the generic cookie is written as a
// backward-compat fallback.
export const setActiveSellerGroupId = (
  sellerGroupId: string,
  profile?: PortalProfile,
): void => {
  activeSellerGroupId = sellerGroupId;
  if (profile) activeProduct = profile;

  if (typeof document === 'undefined') return;

  const cookieName = profile
    ? getProductCookieName(profile)
    : ACTIVE_SELLER_GROUP_COOKIE;

  writeCookie(cookieName, sellerGroupId);
  if (profile) writeCookie(ACTIVE_PRODUCT_COOKIE, profile);
};

export const getActiveSellerGroupId = (): string | null => {
  // Layer 0: standalone-runtime query-string context always wins
  // (LOCAL e deploy preview — nunca produção)
  if (isStandaloneLoginRuntime()) {
    const fromLocation = getActiveSellerGroupIdFromLocation();
    if (fromLocation) return fromLocation;
  }

  // Layer 1: in-memory (fastest, isolated per tab JS context)
  if (activeSellerGroupId) return activeSellerGroupId;

  // Layer 2: product-scoped cookie (isolated per product, survives hard refresh)
  const product = detectCurrentProduct();
  if (product) {
    const productValue = getCookieValue(getProductCookieName(product));
    if (productValue) {
      activeSellerGroupId = productValue;
      return productValue;
    }
  }

  // Layer 3: generic cookie (backward compat for sessions active at deploy time)
  const genericValue = getCookieValue(ACTIVE_SELLER_GROUP_COOKIE);
  if (genericValue) {
    activeSellerGroupId = genericValue;
  }
  return genericValue;
};

export const getValidActiveSellerGroupId = (): string | null => {
  const sellerGroupId = getActiveSellerGroupId();

  if (!sellerGroupId || !UUID_REGEX.test(sellerGroupId)) {
    return null;
  }

  // Runtime standalone (LOCAL / deploy preview) seleciona o seller group
  // explicitamente pela query string (Layer 0 acima) — não há risco de cookie
  // cross-tenant a proteger aqui, então validar o formato basta. Fora dele o
  // caminho estrito abaixo continua valendo, inclusive em produção.
  if (isStandaloneLoginRuntime()) {
    return sellerGroupId;
  }

  const product = detectCurrentProduct();
  const validIds = product && validSellerGroupIdsByProduct.get(product);

  // No candidates registered yet for this product — bootstrap hasn't
  // resolved /portal/me, so there is nothing to validate the cookie against
  // yet. Fail closed: never let an unvalidated seller group id leave in a
  // request header (LOG-475, Gap 1).
  if (!validIds) return null;

  return validIds.has(sellerGroupId) ? sellerGroupId : null;
};

export const buildLocalPortalDashboardUrl = (
  context: PortalSellerGroupContext,
  // Base do redirect. DEFAULT `/` (landing padrão) — é o que o SELETOR de contexto
  // (login-context.page) e os redirects querem: sair da tela de escolha pra dentro
  // do app. Preservar o pathname ali fazia o seletor navegar PRA SI MESMO → loop.
  // Só o TOGGLE de homologação passa `window.location.pathname`, pra trocar de
  // ambiente sem sair da página (ex.: continuar no /dashboard).
  basePath = '/',
): string => {
  const params = new URLSearchParams({
    [PRODUCT_PARAM]: context.profile,
    [SELLER_GROUP_PARAM]: context.id,
  });
  return `${basePath}?${params.toString()}`;
};

/**
 * Monta a URL do portal carregando o seller group em que o usuário estava.
 *
 * Usada no redirect do domínio legado após a virada: o portal valida esse id
 * contra o /portal/me da sessão e, se ela não tiver acesso, segura o usuário
 * na tela de seleção em vez de mandá-lo a um ambiente que não é dele. Por isso
 * o parâmetro não precisa ser assinado — ele só escolhe entre contextos que o
 * backend já autorizou.
 */
export const buildPortalHandoffUrl = (
  portalUrl: string,
  sellerGroupId?: string | null,
): string => {
  if (!portalUrl || !sellerGroupId) return portalUrl;

  const separator = portalUrl.includes('?') ? '&' : '?';

  return `${portalUrl}${separator}${SELLER_GROUP_PARAM}=${encodeURIComponent(
    sellerGroupId,
  )}`;
};

export const buildPortalContextErrorUrl = (portalUrl: string): string => {
  const isAbsolute = /^https?:\/\//i.test(portalUrl);
  const base =
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  const url = new URL(portalUrl || '/', base);

  url.search = '';
  url.hash = '';
  url.searchParams.set('select_context', 'true');
  url.searchParams.set('error', 'no-active-sellers-in-context');

  return isAbsolute ? url.toString() : `${url.pathname}${url.search}`;
};
